import type { TestModule, TestVariant } from './tests';

export type MarkReason = 'CORRECT' | 'BLANK' | 'WRONG' | 'OVER_WORD_LIMIT';

export interface Mark {
  questionNumber: number;
  given: string;
  correct: boolean;
  reason: MarkReason;
  accepted: string[];
  part: number | null;
  type: string | null;
}

export interface WritingCriteria {
  taskAchievement: number;
  coherenceCohesion: number;
  lexicalResource: number;
  grammaticalRange: number;
}

export interface WritingTaskMark {
  task: 1 | 2;
  criteria: WritingCriteria;
  band: number;
  comments: string | null;
}

/** The four official criteria, with what each one is asking about. */
export const CRITERIA: { key: keyof WritingCriteria; label: string; task1: string; task2: string }[] = [
  {
    key: 'taskAchievement',
    label: 'Task achievement',
    task1: 'Is every part of the data covered, accurately, with an overview?',
    task2: 'Is the question answered fully, with a clear position and developed ideas?',
  },
  {
    key: 'coherenceCohesion',
    label: 'Coherence and cohesion',
    task1: 'Is the information ordered logically and linked clearly?',
    task2: 'Does the argument progress logically, with paragraphs used well?',
  },
  {
    key: 'lexicalResource',
    label: 'Lexical resource',
    task1: 'Range and accuracy of vocabulary, including how data is described.',
    task2: 'Range and accuracy of vocabulary, including collocation and precision.',
  },
  {
    key: 'grammaticalRange',
    label: 'Grammatical range and accuracy',
    task1: 'Variety of structures, and how often errors interfere with meaning.',
    task2: 'Variety of structures, and how often errors interfere with meaning.',
  },
];

/** Bands are whole or half, never anything between. */
export const BAND_CHOICES = [0, 1, 2, 3, 4, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9];

/**
 * What the sittings table can be ordered by. The server holds the same list
 * and refuses anything else, because this arrives as a query parameter.
 */
export type ResultSortKey =
  | 'graded'
  | 'candidate'
  | 'student'
  | 'paper'
  | 'score'
  | 'band'
  | 'released';

export interface ResultRow {
  id: string;
  rawScore: number;
  total: number;
  /** Null while a Writing paper waits for an examiner. */
  bandScore: number | null;
  awaitingMarking: boolean;
  writingMarks: WritingTaskMark[] | null;
  markedAt: string | null;
  bandTable: string;
  perPart: { index: number; correct: number; total: number }[];
  gradedAt: string;
  releasedAt: string | null;
  test: {
    id: string;
    title: string;
    module: TestModule;
    variant: TestVariant;
    /** The whole test this paper belongs to, where it belongs to one. */
    setName: string | null;
  };
  student: { id: string; fullName: string; candidateNumber: string | null };
  session: { id: string; submittedAt: string | null; autoSubmitted: boolean };
}

export type ResultDetail = ResultRow & {
  /** Whether this centre lets candidates go back over the paper. */
  reviewEnabled?: boolean;
  marks: Mark[];
  module: 'LISTENING' | 'READING' | 'WRITING';
  /** Writing only: what the candidate wrote, by task number. */
  essays: Record<number, string> | null;
  /**
   * When the scripts were cleared, if they were. Marked Writing papers keep
   * their bands for good but shed the essays two days after marking, so a
   * missing script here means "cleared", not "wrote nothing".
   */
  scriptsClearedAt: string | null;
  /** Writing only: how long each task ran to. Outlives the script itself. */
  scriptWords: Record<number, number> | null;
  prompts:
    | { task: number; instructions: string | null; promptHtml: string | null; imageUrl: string | null }[]
    | null;
};

export interface QuestionStat {
  questionNumber: number;
  sat: number;
  correct: number;
  blank: number;
  overLimit: number;
  percentCorrect: number;
}

export const REASON_LABEL: Record<MarkReason, string> = {
  CORRECT: 'Correct',
  WRONG: 'Incorrect',
  BLANK: 'No answer',
  OVER_WORD_LIMIT: 'Over the word limit',
};

/** Bands are always shown to one decimal: 7 is written 7.0, as on a report form. */
export const band = (value: number | null) => (value === null ? '—' : value.toFixed(1));

/** The task band an examiner's four scores imply, shown live as they choose. */
export const taskBandOf = (c: WritingCriteria): number => {
  const values = [c.taskAchievement, c.coherenceCohesion, c.lexicalResource, c.grammaticalRange];
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 2) / 2;
};

/** Task 2 counts double, as it does in the real exam. */
export const writingBandOf = (one: number, two: number): number =>
  Math.round(((one + 2 * two) / 3) * 2) / 2;

export interface ScoreCell {
  resultId: string;
  testTitle: string;
  band: number | null;
  rawScore: number;
  total: number;
  awaitingMarking: boolean;
}

/** One candidate's three papers, as the mark book shows them. */
export interface ScoreRow {
  student: { id: string; fullName: string; candidateNumber: string | null };
  listening: ScoreCell | null;
  reading: ScoreCell | null;
  writing: ScoreCell | null;
  average: number | null;
  awaitingWriting: boolean;
}
